/** Overview "Routing decisions" card — By country/CDN/site/domain rankings from
 * /waf/historical/top-optimizations. */
import { useEffect, useMemo, useState } from 'react';
import { Route } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, SegmentedControl, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { BarCard } from '@/pages/services/_shared/AnalyticsKit';
import { useWafTopOptimizations, useWafPlatforms, type WafHomeScope } from '@/api/waf';
import { countryName } from '../_shared/geo';
import { fmtNum } from './format';
import styles from './WafDashboard.module.css';

/** Owl supports cdn / domain / country per site; the `site` ranking only exists
 * for the org-wide overview (All site + All domain). Resolve the category list
 * from the scope — shared with the page's Routing-Decision KPI so they share the
 * same cached query. */
export function routeCats(scope: WafHomeScope): string[] {
  return scope.siteId ? ['country', 'cdn', 'domain'] : ['country', 'cdn', 'domain', 'site'];
}

const CAT_LABEL: Record<string, string> = {
  country: 'By country',
  cdn: 'By CDN',
  domain: 'By domain',
  site: 'By site',
};

export function RoutingCard({ serviceId, scope }: { serviceId: string; scope: WafHomeScope }) {
  const cats = routeCats(scope);
  const [cat, setCat] = useState('country');
  // 'site' disappears once a concrete site is picked — fall back to a valid tab.
  useEffect(() => {
    if (!cats.includes(cat)) setCat(cats[0]);
  }, [cats, cat]);

  const q = useWafTopOptimizations(serviceId, scope, cats);
  const total = q.data?.total_count ?? 0;

  // CDN keys come back as platform IDs — resolve to display names. ("0" = Origin.)
  const platformsQ = useWafPlatforms(serviceId);
  const cdnLabel = useMemo(() => {
    const m = new Map((platformsQ.data ?? []).map((p) => [p.id, p.display_name || p.name]));
    return (id: string) => (id === '0' ? 'Origin' : m.get(id) ?? id);
  }, [platformsQ.data]);

  const items = useMemo(() => {
    const found = (q.data?.data ?? []).find((c) => c.category === cat);
    return (found?.data ?? [])
      .map((b) => ({
        label:
          cat === 'cdn'
            ? cdnLabel(b.key)
            : cat === 'country' && b.key.length <= 3
              ? countryName(b.key)
              : b.key,
        value: b.count,
        display: fmtNum(b.count),
      }))
      .sort((a, b) => b.value - a.value);
  }, [q.data, cat, cdnLabel]);

  return (
    <Card>
      <CardHeader
        actions={
          <SegmentedControl
            items={cats.map((c) => ({ value: c, label: CAT_LABEL[c] ?? c }))}
            value={cat}
            onValueChange={setCat}
            size="sm"
            aria-label="Group by"
          />
        }
      >
        <CardTitle>Routing decisions</CardTitle>
      </CardHeader>
      <CardBody>
        <p className={styles.chartTotal}>Total routing decisions: {total.toLocaleString()}</p>
        {q.isPending ? (
          <Skeleton style={{ height: 200 }} />
        ) : items.length === 0 ? (
          <EmptyState size="sm" icon={<Route size={20} />} title="No routing decisions yet" description="No routing decisions recorded in the selected period." />
        ) : (
          <BarCard title="" items={items} />
        )}
      </CardBody>
    </Card>
  );
}

export default RoutingCard;
